import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Line } from 'react-chartjs-2';
import 'chart.js/auto';
import api from '../api';
import PageLayout from './PageLayout';
import { theme, t } from '../theme';
import {
  FaChartLine, FaShoppingCart, FaReceipt, FaMoneyBillWave,
  FaExclamationTriangle, FaArrowUp, FaArrowDown, FaTruck,
  FaBirthdayCake, FaBoxOpen, FaPlus, FaFileAlt, FaWallet, FaArrowDown as FaDown,
} from 'react-icons/fa';
import NovaTransacaoModal from './NovaTransacaoModal';
import ExtratoMensalModal from './ExtratoMensalModal';
import useIsMounted from '../hooks/useIsMounted';

const PERIODOS = [
  { id: 'hoje', label: 'Hoje' },
  { id: '7d', label: '7 dias' },
  { id: '15d', label: '15 dias' },
  { id: '30d', label: '30 dias' },
  { id: 'mes_atual', label: 'Este mês' },
];

const fmt = (v) => `R$ ${(v || 0).toFixed(2).replace('.', ',')}`;
const fmtData = (d) => d ? new Date(d + 'T00:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) : '';

const STATUS_CORES = {
  confirmado: theme.colors.success, em_producao: theme.colors.warning,
  pronto: theme.colors.info, entregue: '#7f8c8d', cancelado: theme.colors.danger,
  // compat com pedidos legados
  pendente: theme.colors.success,
};
const STATUS_LABELS = {
  confirmado: 'Confirmado', em_producao: 'Em produção', pronto: 'Pronto',
  entregue: 'Retirado', cancelado: 'Cancelado',
  pendente: 'Confirmado',
};

const Dashboard = () => {
  const [data, setData] = useState(null);
  const [periodo, setPeriodo] = useState('30d');
  const [loading, setLoading] = useState(true);
  const [showNovaTransacao, setShowNovaTransacao] = useState(false);
  const [showExtrato, setShowExtrato] = useState(false);
  const navigate = useNavigate();
  const mounted = useIsMounted();

  useEffect(() => { carregar(); }, [periodo]);

  const carregar = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/api/dashboard?periodo=${periodo}`);
      if (!mounted.current) return;
      setData(res.data);
    } catch {} finally { if (mounted.current) setLoading(false); }
  };

  const variacao = (atual, anterior) => {
    if (!anterior || anterior === 0) return null;
    return ((atual - anterior) / anterior * 100).toFixed(0);
  };

  if (loading || !data) {
    return (
      <PageLayout title="Painel">
        <div style={{ textAlign: 'center', padding: 80, color: theme.colors.textMuted }}>Carregando...</div>
      </PageLayout>
    );
  }

  const varRec = variacao(data.receitas, data.receitas_anterior);
  const varDes = variacao(data.despesas, data.despesas_anterior);
  const varLucro = variacao(data.lucro, data.lucro_anterior);
  const varPed = variacao(data.total_pedidos, data.pedidos_anterior);

  // Gráfico Receitas × Despesas
  const serieRxD = data.serie_receitas_x_despesas || [];
  const chartData = {
    labels: serieRxD.map(d => fmtData(d.data)),
    datasets: [
      {
        label: 'Receitas',
        data: serieRxD.map(d => d.receitas || 0),
        borderColor: '#16a34a',
        backgroundColor: '#16a34a22',
        fill: true,
        tension: 0.4,
        pointRadius: 2,
        pointBackgroundColor: '#16a34a',
      },
      {
        label: 'Despesas',
        data: serieRxD.map(d => d.despesas || 0),
        borderColor: '#dc2626',
        backgroundColor: '#dc262622',
        fill: true,
        tension: 0.4,
        pointRadius: 2,
        pointBackgroundColor: '#dc2626',
      },
    ],
  };
  const chartOptions = {
    responsive: true, maintainAspectRatio: false,
    plugins: {
      legend: { display: true, position: 'top', labels: { font: { size: 12 }, usePointStyle: true } },
      tooltip: { callbacks: { label: (ctx) => `${ctx.dataset.label}: ${fmt(ctx.parsed.y)}` } },
    },
    scales: {
      x: { grid: { display: false }, ticks: { font: { size: 11 }, color: theme.colors.textMuted } },
      y: { grid: { color: theme.colors.borderLight }, ticks: { font: { size: 11 }, color: theme.colors.textMuted, callback: (v) => `R$ ${v}` } },
    },
  };

  return (
    <PageLayout title="Painel">
      {/* Topo: filtro + ações financeiras */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
        <div className="period-pills">
          {PERIODOS.map(p => (
            <button key={p.id} onClick={() => setPeriodo(p.id)}
              style={{
                padding: '7px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                border: `1.5px solid ${periodo === p.id ? theme.colors.primary : theme.colors.border}`,
                background: periodo === p.id ? theme.colors.primary : '#fff',
                color: periodo === p.id ? '#fff' : theme.colors.textSecondary,
                transition: 'all 0.15s',
              }}>
              {p.label}
            </button>
          ))}
        </div>
        <div style={{ flex: 1 }}></div>
        <div className="page-actions" style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setShowExtrato(true)} style={t.btnSecondary}><FaFileAlt /> Extrato</button>
          <button onClick={() => setShowNovaTransacao(true)} style={t.btnPrimary}><FaPlus /> Nova Transação</button>
        </div>
      </div>

      {/* Linha 1: KPIs financeiros */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 16 }}>
        <KpiCard icon={<FaArrowUp />} label="Receitas" valor={fmt(data.receitas)}
          variacao={varRec} cor="#16a34a" corLight="#f0fdf4" />
        <KpiCard icon={<FaDown />} label="Despesas" valor={fmt(data.despesas)}
          variacao={varDes} variacaoInvertida cor="#dc2626" corLight="#fef2f2" />
        <KpiCard icon={<FaMoneyBillWave />} label="Lucro Líquido" valor={fmt(data.lucro)}
          variacao={varLucro} cor={(data.lucro || 0) >= 0 ? '#16a34a' : '#dc2626'} corLight={(data.lucro || 0) >= 0 ? '#f0fdf4' : '#fef2f2'} />
        <KpiCard icon={<FaWallet />} label="Saldo de Caixa" valor={fmt(data.saldo_caixa)}
          cor={theme.colors.primary} corLight={theme.colors.primaryLight} />
      </div>

      {/* Linha 2: KPIs operacionais */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
        <KpiCard icon={<FaShoppingCart />} label="Pedidos" valor={data.total_pedidos}
          variacao={varPed} cor={theme.colors.info} corLight={theme.colors.infoLight} />
        <KpiCard icon={<FaReceipt />} label="Ticket Médio" valor={fmt(data.ticket_medio)}
          cor={theme.colors.secondary} corLight={theme.colors.bgSection} />
        <KpiCard icon={<FaMoneyBillWave />} label="A Receber" valor={fmt(data.a_receber)}
          cor={theme.colors.warning} corLight={theme.colors.warningLight} />
      </div>

      {/* Gráfico Receitas × Despesas */}
      <div style={{ ...t.card, padding: '20px 24px', marginBottom: 24 }}>
        <h3 style={{ ...t.h3, marginBottom: 16 }}>Receitas × Despesas</h3>
        <div style={{ height: 260 }}>
          {serieRxD.length > 0 ? (
            <Line data={chartData} options={chartOptions} />
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: theme.colors.textMuted }}>
              <div style={{ textAlign: 'center' }}>
                <FaChartLine size={32} style={{ marginBottom: 8, color: theme.colors.border }} />
                <p>Sem transações no período</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Mini Saldo por Método */}
      {(data.saldo_por_metodo || []).length > 0 && (
        <div style={{ ...t.card, padding: '20px 24px', marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={t.h3}><FaWallet style={{ marginRight: 8, color: theme.colors.textMuted }} />Saldo por Método</h3>
            <button onClick={() => navigate('/financas?tab=caixa')} style={{ ...t.btnGhost, fontSize: 12 }}>Ver tudo →</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
            {data.saldo_por_metodo.map((m, i) => (
              <div key={i} style={{ background: theme.colors.bgSection, borderRadius: 10, padding: 12 }}>
                <div style={{ fontSize: 12, color: theme.colors.textMuted, marginBottom: 4 }}>{m.metodo}</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: m.saldo >= 0 ? '#16a34a' : '#dc2626' }}>{fmt(m.saldo)}</div>
                <div style={{ fontSize: 11, color: theme.colors.textMuted, marginTop: 4 }}>
                  ↑ {fmt(m.entradas)} · ↓ {fmt(m.saidas)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Grid inferior */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16, marginBottom: 24 }}>
        {/* Próximas entregas */}
        <div style={{ ...t.card, padding: '20px 24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={t.h3}><FaTruck style={{ marginRight: 8, color: theme.colors.textMuted }} />Próximas Entregas</h3>
            <button onClick={() => navigate('/pedidos')} style={{ ...t.btnGhost, fontSize: 12 }}>Ver todos →</button>
          </div>
          {(data.proximas_entregas || []).length === 0 ? (
            <div style={{ textAlign: 'center', padding: 24, color: theme.colors.textMuted, fontSize: 14 }}>Nenhuma entrega agendada</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {data.proximas_entregas.map(e => (
                <div key={e.id} onClick={() => navigate('/pedidos')}
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', borderRadius: 10, background: theme.colors.bgSection, cursor: 'pointer', transition: 'background 0.1s' }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14, color: theme.colors.text }}>{e.cliente_nome}</div>
                    <div style={{ fontSize: 12, color: theme.colors.textMuted }}>
                      {fmtData(e.data_entrega)} {e.horario_entrega || ''}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontWeight: 600, fontSize: 14, color: theme.colors.primary }}>{fmt(e.valor_total)}</div>
                    <span style={{ fontSize: 11, fontWeight: 600, color: STATUS_CORES[e.status], background: `${STATUS_CORES[e.status]}15`, padding: '2px 8px', borderRadius: 10 }}>
                      {STATUS_LABELS[e.status]}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Bolos populares */}
        <div style={{ ...t.card, padding: '20px 24px' }}>
          <h3 style={{ ...t.h3, marginBottom: 16 }}><FaBirthdayCake style={{ marginRight: 8, color: theme.colors.textMuted }} />Bolos Mais Pedidos</h3>
          {(data.bolos_populares || []).length === 0 ? (
            <div style={{ textAlign: 'center', padding: 24, color: theme.colors.textMuted, fontSize: 14 }}>Sem dados ainda</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {data.bolos_populares.map((b, i) => {
                const maxTotal = data.bolos_populares[0]?.total || 1;
                const pct = (b.total / maxTotal) * 100;
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ width: 24, textAlign: 'center', fontSize: 13, fontWeight: 700, color: i === 0 ? theme.colors.primary : theme.colors.textMuted }}>{i + 1}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ fontSize: 14, fontWeight: 500, color: theme.colors.text }}>{b.tamanho_nome}</span>
                        <span style={{ fontSize: 13, fontWeight: 600, color: theme.colors.textSecondary }}>{b.total} pedido{b.total !== 1 ? 's' : ''}</span>
                      </div>
                      <div style={{ height: 6, background: theme.colors.borderLight, borderRadius: 3, overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${pct}%`, background: theme.colors.primary, borderRadius: 3, transition: 'width 0.5s' }} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Alertas de estoque */}
      {(data.alertas_estoque?.produtos_baixo > 0 || data.alertas_estoque?.embalagens_baixo > 0) && (
        <div style={{ ...t.card, padding: '20px 24px' }}>
          <h3 style={{ ...t.h3, marginBottom: 16 }}>
            <FaExclamationTriangle style={{ marginRight: 8, color: theme.colors.warning }} />
            Alertas de Estoque
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 8 }}>
            {(data.alertas_estoque?.produtos || []).map((p, i) => (
              <div key={i} onClick={() => navigate('/estoque')}
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderRadius: 10, background: theme.colors.warningLight, cursor: 'pointer' }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 500, color: theme.colors.text }}>{p.nome}</div>
                  <div style={{ fontSize: 12, color: theme.colors.textMuted }}>Mín: {p.estoque_minimo}</div>
                </div>
                <span style={{ fontSize: 18, fontWeight: 700, color: theme.colors.danger }}>{p.estoque_atual}</span>
              </div>
            ))}
            {(data.alertas_estoque?.embalagens || []).map((e, i) => (
              <div key={`e${i}`} onClick={() => navigate('/embalagens')}
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderRadius: 10, background: theme.colors.dangerLight, cursor: 'pointer' }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 500, color: theme.colors.text }}>{e.nome}</div>
                  <div style={{ fontSize: 12, color: theme.colors.textMuted }}>Embalagem · Mín: {e.estoque_minimo}</div>
                </div>
                <span style={{ fontSize: 18, fontWeight: 700, color: theme.colors.danger }}>{e.estoque_atual}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {showNovaTransacao && (
        <NovaTransacaoModal onClose={() => setShowNovaTransacao(false)} onSaved={() => { setShowNovaTransacao(false); carregar(); }} />
      )}
      {showExtrato && <ExtratoMensalModal onClose={() => setShowExtrato(false)} />}
    </PageLayout>
  );
};


// KPI Card
const KpiCard = ({ icon, label, valor, variacao, variacaoInvertida, cor, corLight }) => (
  <div className="kpi-card" style={{ ...t.card, padding: '20px 24px' }}>
    <div className="kpi-card-head" style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
      <div className="kpi-card-icon" style={{ width: 36, height: 36, borderRadius: 10, background: corLight, display: 'flex', alignItems: 'center', justifyContent: 'center', color: cor, fontSize: 16 }}>
        {icon}
      </div>
      <span className="kpi-card-label" style={{ fontSize: 13, fontWeight: 600, color: theme.colors.textSecondary }}>{label}</span>
    </div>
    <div className="kpi-card-value" style={{ fontSize: 26, fontWeight: 700, color: theme.colors.text, letterSpacing: '-0.02em' }}>
      {valor}
    </div>
    {variacao !== null && variacao !== undefined && (
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 6, fontSize: 13, fontWeight: 600,
        color: (variacaoInvertida ? variacao <= 0 : variacao >= 0) ? theme.colors.success : theme.colors.danger }}>
        {variacao >= 0 ? <FaArrowUp size={10} /> : <FaArrowDown size={10} />}
        {Math.abs(variacao)}% vs período anterior
      </div>
    )}
  </div>
);


export default Dashboard;
